import { useEffect, useState } from 'react';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Facility, FacilityType, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';
import { BookingsPanel } from './BookingsPanel';

const FACILITY_TYPES: FacilityType[] = ['COMMUNITY_HALL', 'SPORTS_COURT', 'MULTIPURPOSE_GROUND', 'MEETING_ROOM', 'OTHER'];

/**
 * Facility booking management — GET /facilities?district=<own district>
 * (public endpoint, but always scoped to the signed-in persona's own
 * district here), POST /facilities + PATCH /facilities/:id
 * (DISTRICT_ADMINISTRATOR only). Selecting a facility loads its bookings in
 * BookingsPanel, which is where confirm/cancel/complete actually happen.
 */
export function FacilitiesConsole() {
  const { persona } = useAuth();
  const district = persona?.district ?? '';
  const isAdmin = persona?.roles.includes('DISTRICT_ADMINISTRATOR') ?? false;

  const facilitiesQ = useApiQuery<Page<Facility>>(
    district ? `/api/v1/m41/facilities?district=${encodeURIComponent(district)}&limit=100` : null,
  );
  const facilities = facilitiesQ.data?.items ?? [];
  const [selectedId, setSelectedId] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Facility | null>(null);

  useEffect(() => {
    if (!selectedId && facilities.length > 0) setSelectedId(facilities[0]!.id);
  }, [facilities, selectedId]);

  const selected = facilities.find((f) => f.id === selectedId) ?? null;

  if (!district) return <ErrorNote message="This account has no district binding on file." />;

  return (
    <div>
      <PageHeader
        title="Facility Bookings"
        subtitle="Community halls, courts and grounds bookable in this district."
        actions={
          isAdmin ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + New facility
            </button>
          ) : undefined
        }
      />

      {facilitiesQ.error ? (
        <div className="mb-4">
          <ErrorNote message={facilitiesQ.error} />
        </div>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-[320px_1fr]">
        <div className="card h-fit overflow-hidden">
          {facilitiesQ.loading ? (
            <Spinner />
          ) : facilities.length === 0 ? (
            <EmptyState title="No facilities yet" hint={isAdmin ? 'Create one to get started.' : 'Ask your district administrator to register one.'} />
          ) : (
            <ul className="divide-y divide-slate-100">
              {facilities.map((f) => (
                <li key={f.id}>
                  <button
                    onClick={() => setSelectedId(f.id)}
                    className={`w-full px-4 py-3 text-left transition ${selectedId === f.id ? 'bg-brand-50' : 'hover:bg-slate-50'}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-slate-800">{f.name}</span>
                      <StatusBadge status={f.active ? 'ACTIVE' : 'CLOSED'} />
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {f.code} · {f.type.replaceAll('_', ' ')}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-400">
                      {scr(f.hourlyRateScr)}/hr{f.capacity ? ` · capacity ${f.capacity}` : ''}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          {selected ? (
            <>
              <div className="card mb-5 p-5">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-sm font-bold text-slate-700">{selected.name}</h2>
                  {isAdmin ? (
                    <button className="btn-secondary" onClick={() => setEditing(selected)}>
                      Edit
                    </button>
                  ) : null}
                </div>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
                  <div>
                    <dt className="text-xs text-slate-500">Type</dt>
                    <dd className="font-medium text-slate-800">{selected.type.replaceAll('_', ' ')}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">Rate</dt>
                    <dd className="font-medium text-slate-800">{scr(selected.hourlyRateScr)}/hr</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">Capacity</dt>
                    <dd className="font-medium text-slate-800">{selected.capacity ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">Status</dt>
                    <dd>
                      <StatusBadge status={selected.active ? 'ACTIVE' : 'CLOSED'} />
                    </dd>
                  </div>
                </dl>
              </div>
              <BookingsPanel facility={selected} />
            </>
          ) : (
            <div className="card">
              <EmptyState title="Select a facility" hint="Pick one from the list to manage its bookings." />
            </div>
          )}
        </div>
      </div>

      {showCreate ? (
        <CreateFacilityModal
          district={district}
          onClose={() => setShowCreate(false)}
          onDone={(created) => {
            setShowCreate(false);
            facilitiesQ.reload();
            setSelectedId(created.id);
          }}
        />
      ) : null}
      {editing ? (
        <EditFacilityModal
          facility={editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            facilitiesQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateFacilityModal({ district, onClose, onDone }: { district: string; onClose: () => void; onDone: (created: Facility) => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<FacilityType>('COMMUNITY_HALL');
  const [capacity, setCapacity] = useState('');
  const [hourlyRateScr, setHourlyRateScr] = useState('0');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<Facility>('/api/v1/m41/facilities', {
        district,
        code: code.trim(),
        name: name.trim(),
        type,
        capacity: capacity.trim() ? Number.parseInt(capacity, 10) : undefined,
        hourlyRateScr: Number.parseFloat(hourlyRateScr || '0'),
      });
      onDone(created);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create facility');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New facility" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code">
            <input className="field-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. BV-HALL-02" />
          </Field>
          <Field label="Type">
            <select className="field-input" value={type} onChange={(e) => setType(e.target.value as FacilityType)}>
              {FACILITY_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Name">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Beau Vallon Community Hall" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Capacity (optional)">
            <input className="field-input" type="number" min="1" value={capacity} onChange={(e) => setCapacity(e.target.value)} />
          </Field>
          <Field label="Hourly rate (SCR)">
            <input className="field-input" type="number" min="0" step="0.01" value={hourlyRateScr} onChange={(e) => setHourlyRateScr(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || code.trim().length < 2 || name.trim().length < 2}>
            {busy ? 'Creating…' : 'Create facility'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function EditFacilityModal({ facility, onClose, onDone }: { facility: Facility; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(facility.name);
  const [capacity, setCapacity] = useState(facility.capacity ? String(facility.capacity) : '');
  const [hourlyRateScr, setHourlyRateScr] = useState(String(facility.hourlyRateScr));
  const [active, setActive] = useState(facility.active);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/v1/m41/facilities/${facility.id}`, {
        name: name.trim() || undefined,
        capacity: capacity.trim() ? Number.parseInt(capacity, 10) : undefined,
        hourlyRateScr: hourlyRateScr.trim() ? Number.parseFloat(hourlyRateScr) : undefined,
        active,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to update facility');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Edit ${facility.code}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Name">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Capacity">
            <input className="field-input" type="number" min="1" value={capacity} onChange={(e) => setCapacity(e.target.value)} />
          </Field>
          <Field label="Hourly rate (SCR)">
            <input className="field-input" type="number" min="0" step="0.01" value={hourlyRateScr} onChange={(e) => setHourlyRateScr(e.target.value)} />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active (bookable)
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || name.trim().length < 2}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
